import { useQuery } from "@tanstack/react-query";
import { type FeatureFlag, useFeatureFlagsContext } from "../contexts/FeatureFlagsProvider";
import { backendService } from "../services/backend.service";

export const featureFlagQueryKeys = {
	single: (flagKey: string, departmentId?: string, defaultValue?: boolean) => ["openfeature-flag", flagKey, departmentId, defaultValue] as const,
	batch: (flagKeys: string[], departmentId: string) => ["openfeature-flags-batch", flagKeys.join(","), departmentId] as const,
} as const;

export function useOpenFeatureFlags(flagKeys: string[], departmentId: string, options?: { enabled?: boolean; staleTime?: number; gcTime?: number }) {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: featureFlagQueryKeys.batch(flagKeys, departmentId),
		queryFn: async () => {
			const response = await backendService.getFeatureFlagsBatch(flagKeys, departmentId);

			const processedFlags: Record<string, boolean> = {};
			Object.entries(response.data).forEach(([key, value]) => {
				processedFlags[key] = value;
			});

			return processedFlags;
		},
		enabled: options?.enabled !== false && !!departmentId && flagKeys.length > 0,
		staleTime: options?.staleTime ?? 0,
		gcTime: options?.gcTime ?? 10 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	return {
		data: data || {},
		isLoading,
		error,
		refetch,
	};
}

export function useFeatureFlag(flagKey: FeatureFlag, defaultValue: boolean = false): boolean {
	const flags = useFeatureFlagsContext();

	if (flags?.flags[flagKey] !== undefined) {
		return flags.flags[flagKey];
	}

	return defaultValue;
}
